/**
 * `worktree.integrate` (ADR 0086 phase 1, owner decision of 28 Oct 2026): when a mission's agent
 * has finished in its worktree, Orgus commits what is left, merges the project's main branch into
 * the branch and hands it to landing, then records it landed once it is on main. The machine
 * action's params and result, and what the hub keeps on the mission (`Mission.integration`).
 */
import type { Id, Ts } from '../index.ts';

/** The params of `worktree.integrate`. */
export interface WorktreeIntegrateParams {
  checkoutId: Id;
  /** The branch the hub expects the worktree to be on; any other is refused. */
  branch: string;
  /** The branch to merge in and land on; absent: orgus.yml flow.main, else the project's default. */
  main?: string;
  missionId: Id;
  /** The mission title: with the id, the message of the commit of uncommitted work. */
  title: string;
  /** The mission session whose work this is (QA-81): flow.land gets it as ORGUS_SESSION_ID, so the landing queue tells its mission. */
  sessionId?: string;
  /**
   * 'queue' (R-FN-3): commit only, then answer `ready`; the hub's landing queue rebases onto
   * origin's main, verifies, pushes and records the landing. Absent: the local fallback for a
   * project with no remote (merge main, test, fast-forward the local main; never pushes).
   */
  handoff?: 'queue';
}

/**
 * What the action reached. landed: the branch is on main. submitted: the project's flow.land ran
 * and the branch is not on main yet (a queue lands it later). conflict: the merge of main stopped
 * on conflicts and was aborted. check-failed: the default landing's test command failed.
 * land-failed: flow.land exited non-zero. ff-refused: the default landing could not fast-forward
 * the main checkout (not on main, not clean, or main moved). nothing-to-land (QA-91): the branch
 * has no commits ahead of main (the work was already there), so nothing was merged or landed.
 * ready (handoff 'queue'): committed, nothing merged; the hub submits it to its landing queue.
 */
export type IntegrateOutcome = 'ready' | 'landed' | 'submitted' | 'nothing-to-land' | 'conflict' | 'check-failed' | 'land-failed' | 'ff-refused';

/** QA-91: what a mission page says when its branch had nothing to land. */
export const NOTHING_TO_LAND_NOTE = 'The work was already on main: the branch had no new commits, so there was nothing to land.';

/** QA-91: the agent's report says the work was already on main (its item can go straight to done). */
export function reportSaysAlreadyOnMain(text: string | null | undefined): boolean {
  return !!text && /already (on|in) (main|the main branch)|already (landed|merged|done on main)/i.test(text);
}

/** `data` of the action's answer (also on a refusal: `{ code }` only). */
export interface WorktreeIntegrateResult {
  outcome: IntegrateOutcome;
  branch: string;
  main: string;
  /** The commit Orgus made of uncommitted work, when there was any. */
  committed?: string | null;
  /** The merge of main: 'merged', 'up-to-date', or 'conflict'. */
  merge?: 'merged' | 'up-to-date' | 'conflict';
  /** Conflicting files (outcome conflict), relative to the worktree. */
  conflicts?: string[];
  /** How it was handed to landing: the project's flow.land, or the default (test, then fast-forward). */
  landing?: 'flow.land' | 'default';
  /** The command that ran (flow.land, or the test command of the default landing). */
  command?: string;
  /** The branch head after the merge. */
  head?: string;
  /** main's head once the branch is on it (outcome landed). */
  landedSha?: string;
  /** The log of the command, in the machine's Orgus home, and the end of its output. */
  logFile?: string | null;
  tail?: string;
  /** Why it stopped, in one sentence (every outcome but landed and submitted). */
  reason?: string;
  /** The default landing's test selection (ADR 0111 step 10). */
  tests?: IntegrateTests;
}

/**
 * `worktree.landed` (QA-121): is a branch tip on the project's main branch? The hub asks when a
 * mission handed to the landing an hour ago reads submitted and its worktree is gone (the lander
 * removed it after landing, and the landed notice never reached the hub). Read-only: git
 * merge-base --is-ancestor and rev-parse in the project's main checkout, nothing fetched.
 */
export interface WorktreeLandedParams {
  projectId: Id;
  /** The branch tip recorded when the branch was handed to the landing. */
  sha: string;
  /** The mission branch: whether it still exists. */
  branch: string;
  /** The branch it lands on; absent: orgus.yml flow.main, else the project's default. */
  main?: string;
}

/** `data` of `worktree.landed`'s answer. */
export interface WorktreeLandedResult {
  /** The tip is an ancestor of main. */
  onMain: boolean;
  /** The branch still exists locally. */
  branchExists: boolean;
  main: string;
  /** main's head, when the tip is on it. */
  mainHead?: string;
}

/** A tip handed to the landing: hex, 7 to 64 characters (never an option or a ref expression). */
export const isTipSha = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{7,64}$/i.test(v);

/**
 * Which tests a default landing ran (ADR 0111 step 10): the ones the project's runner ties to the
 * changed files (related), or the whole suite (full), and why.
 */
export interface IntegrateTests {
  mode: 'related' | 'full';
  runner?: 'vitest' | 'jest';
  /** The changed source files the related run was given (at most 200). */
  files: string[];
  why: string;
}

/**
 * The hub's record of Orgus integrating a mission (`Mission.integration`). One per launch: a new
 * launch (a follow-up agent) starts a fresh record; a conflict is tried again when the branch's
 * head moves; a transient failure (offline, timeout) is tried again with backoff.
 */
export interface MissionIntegration {
  /** The launch whose finished work this integrates. */
  launchId: Id;
  /**
   * 'stopped': the mission closed before Orgus integrated it (ORGU-273); nothing more runs.
   * 'lost' (QA-121): handed to the landing, its worktree and branch are gone and its tip is not on main.
   */
  status: 'stopping' | 'running' | IntegrateOutcome | 'refused' | 'error' | 'unsupported' | 'stopped' | 'lost';
  /** Tries for this launch (transient failures count). */
  attempts: number;
  at: Ts;
  /** When Orgus may try again after a transient failure. */
  retryAt?: Ts | null;
  /** The worktree's head when it last stopped on a conflict or refusal: a new head tries again. */
  head?: string | null;
  message?: string;
  conflicts?: string[];
  /** The end of the failing command's output (check-failed), clipped: the follow-up agent reads it. */
  tail?: string;
  /** R-FN-3: the hub landing queue's entry this branch was submitted as (status 'submitted'). */
  landingId?: Id | null;
  steps: { at: Ts; what: string; why?: string }[];
  /** The idle session Orgus stopped to integrate (QA-53): it finished the work, whatever its exit reads. */
  stoppedSessionId?: Id | null;
  /** The head whose failed landing Orgus already tried once more (QA-60): a second failure goes to an agent. */
  landRetriedHead?: string | null;
  /** The tests the last landing attempt ran (ADR 0111 step 10). */
  tests?: IntegrateTests;
  /** QA-121: the branch tip handed to the landing (status submitted): checked against main once the worktree is gone. */
  tip?: string | null;
}

/**
 * QA-58 (29 Oct 2026): a setback whose next step is an agent's. Orgus launches a follow-up
 * headless session in the mission's worktree to resolve it, at most INTEGRATE_MAX_FOLLOW_UPS
 * times per mission for each kind, then hands it to a person.
 */
export type FollowUpKind = 'conflict' | 'check-failed';

/** On a launch Orgus started to resolve an integration setback (MissionLaunch.followUp). */
export interface LaunchFollowUp {
  kind: FollowUpKind;
  /** The launch whose integration stopped on the setback. */
  fromLaunchId: Id;
}

/** Follow-up agents per mission for one setback kind before a person takes over. */
export const INTEGRATE_MAX_FOLLOW_UPS = 2;
/** The most of a failing command's output a record (and the follow-up prompt) keeps. */
export const FOLLOW_UP_TAIL_MAX = 2000;

/** QA-121: what a person does when a branch handed to the landing vanished before it landed. */
export const LOST_NEXT_STEP = 'Its branch and worktree are gone and the work never reached main. Retry the mission to do the work again, or restore the branch from its tip and land it';

/** QA-60: how long Orgus waits before landing a branch once more after the project's landing failed. */
export const LAND_RETRY_MS = 60_000;

/** The follow-up kind an integration status calls for, or null when an agent cannot fix it. */
export function followUpKindOf(status: MissionIntegration['status']): FollowUpKind | null {
  // A failed project landing (flow.land) is failing checks for the agent too (QA-60).
  return status === 'conflict' || status === 'check-failed' ? status : status === 'land-failed' ? 'check-failed' : null;
}

/** Follow-up launches of this kind the mission has had (started or not). */
export function followUpCount(m: { launches: readonly { followUp?: LaunchFollowUp }[] }, kind: FollowUpKind): number {
  return m.launches.filter((l) => l.followUp?.kind === kind).length;
}

/** Integrate attempts per launch before Orgus stops trying on its own. */
export const INTEGRATE_MAX_ATTEMPTS = 3;
/** Backoff before the next try after a transient failure, by attempt (ms). */
export const INTEGRATE_BACKOFF_MS: readonly number[] = [60_000, 5 * 60_000, 15 * 60_000];
/**
 * QA-82: a record that reads running while no integrate is under way in this hub (it restarted
 * mid-integrate) is tried again after this long: the machine side refuses a second run while one
 * is alive, and aborts only a merge its own dead run left.
 */
export const INTEGRATE_ORPHAN_MS = 2 * 60_000;
/** How long the hub waits for the machine's answer: the landing may run the project's tests. */
export const INTEGRATE_ACTION_TIMEOUT_MS = 70 * 60_000;
/**
 * Orgus integrates only work whose launch is this recent: older finished missions wait for phase
 * 1's adoption pass (ADR 0086 decision 1), which decides per branch whether it still matters.
 */
export const INTEGRATE_MAX_LAUNCH_AGE_MS = 3 * 24 * 3600_000;
/** The default landing's and flow.land's own limit on the machine. */
export const INTEGRATE_COMMAND_TIMEOUT_S = 3600;

/** The newest launch that started: the one whose work Orgus integrates. */
export function newestLaunch<L extends { result: string }>(m: { launches: readonly L[] }): L | null {
  for (let i = m.launches.length - 1; i >= 0; i--) if (m.launches[i]!.result === 'ok') return m.launches[i]!;
  return null;
}

/** Orgus drives the landing of a mission it started: its newest launch is headless. */
export function orgusLands(m: { launches: readonly { result: string; mode: string }[] }): boolean {
  return newestLaunch(m)?.mode === 'headless';
}

/** Who acts next and what, for an integration record; null when the record says nothing (landed). */
export function integrationNext(
  rec: Pick<MissionIntegration, 'status' | 'attempts' | 'message' | 'conflicts'>,
  /** followUps: the follow-up agents Orgus already started for this kind (QA-58); absent when Orgus does not drive the mission. */
  opts: { followUps?: number } = {},
): { who: 'orgus' | 'agent' | 'landing' | 'person'; what: string } | null {
  const files = (rec.conflicts ?? []).slice(0, 5).join(', ') || 'the branch';
  if (opts.followUps !== undefined && followUpKindOf(rec.status)) {
    const conflict = rec.status === 'conflict';
    if (opts.followUps >= INTEGRATE_MAX_FOLLOW_UPS) {
      return {
        who: 'person',
        what: conflict
          ? `Orgus's agent could not resolve the merge conflict in ${files} after ${opts.followUps} tries. Merge main into the branch, resolve it and commit; Orgus lands it after the commit`
          : `Orgus's agent could not fix the failing checks after ${opts.followUps} tries. Fix them on the branch and commit; Orgus lands it after the commit`,
      };
    }
    return { who: 'agent', what: conflict ? `Orgus starts an agent to resolve the merge conflict in ${files}, then lands the branch` : 'Orgus starts an agent to fix the failing checks, then lands the branch' };
  }
  switch (rec.status) {
    case 'stopping':
    case 'running':
      return { who: 'orgus', what: 'Commit the work, merge main into the branch and land it' };
    case 'submitted':
      return { who: 'landing', what: 'Put the branch on main' };
    case 'landed':
    case 'stopped':
    case 'nothing-to-land':
      return null;
    case 'conflict':
      return { who: 'agent', what: `Resolve the merge conflict in ${files}: merge main into the branch, fix the files and commit. Orgus lands it after the commit` };
    case 'check-failed':
      return { who: 'agent', what: `Fix the failing tests on the branch and commit, then Orgus lands it. ${rec.message ?? ''}`.trim() };
    case 'lost':
      return { who: 'person', what: rec.message ?? LOST_NEXT_STEP };
    case 'unsupported':
      return { who: 'person', what: "This machine's agent cannot merge and land for Orgus yet (the Go agent has no worktree.integrate). Merge main into the branch and land it yourself" };
    case 'error':
      return rec.attempts >= INTEGRATE_MAX_ATTEMPTS ? { who: 'person', what: `Orgus could not land the branch: ${rec.message ?? 'the machine did not answer'}. Land it yourself, or bring the machine back` } : { who: 'orgus', what: 'Try landing the branch again' };
    default:
      return { who: 'person', what: rec.message ?? 'Land the branch yourself' };
  }
}

/**
 * ORGU-273: a closed mission's integration record in a final state. A record left mid-way
 * ('stopping' after the idle session was stopped, or 'running') reads 'landed' when the branch
 * landed, else 'stopped'; any other record, or an open mission, is returned unchanged.
 */
export function settleIntegration<R extends Pick<MissionIntegration, 'status' | 'at' | 'steps' | 'message'>>(
  m: { closed?: { as: string; at: Ts } | null; landed?: unknown; integration?: R | null },
): R | null | undefined {
  const rec = m.integration;
  if (!m.closed || !rec || (rec.status !== 'stopping' && rec.status !== 'running')) return rec;
  const at = Math.max(rec.at, m.closed.at);
  if (m.landed) return { ...rec, status: 'landed', at, steps: [...rec.steps, { at, what: 'The branch landed; the mission closed' }].slice(-30) };
  const what = m.closed.as === 'done' ? 'The mission closed as done before Orgus landed it' : 'The mission was stopped before Orgus landed it';
  return { ...rec, status: 'stopped', at, message: rec.message ?? what, steps: [...rec.steps, { at, what }].slice(-30) };
}

/**
 * QA-56: Orgus is about to land, or is landing, this mission's finished work by itself (the
 * integrator's own launch checks, and no setback that waits for an agent or a person). The page
 * shows such a mission as landing, never as a review step a person must take first.
 */
export function orgusLandingDue(
  m: { launches: readonly { id: Id; result: string; mode: string; at: Ts; branch?: string | null; worktree?: string | null; cwd?: string | null }[]; closed?: unknown; landed?: unknown; paused?: unknown; integration?: Pick<MissionIntegration, 'launchId' | 'status' | 'attempts'> | null },
  now: Ts,
): boolean {
  if (m.closed || m.landed || m.paused) return false;
  const launch = newestLaunch(m);
  if (!launch || launch.mode !== 'headless' || !launch.branch || !(launch.worktree ?? launch.cwd)) return false;
  const rec = m.integration?.launchId === launch.id ? m.integration : null;
  if (!rec) return now - launch.at <= INTEGRATE_MAX_LAUNCH_AGE_MS;
  if (rec.status === 'error') return rec.attempts < INTEGRATE_MAX_ATTEMPTS;
  return rec.status === 'stopping' || rec.status === 'running' || rec.status === 'submitted' || rec.status === 'landed' || rec.status === 'nothing-to-land';
}
